import {
  useContext, useEffect, useMemo, useState,
} from 'react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useLocation, useParams } from 'react-router';
import styled from '@emotion/styled';
import {
  Drawer,
  IconButton,
  Stack,
  SvgIcon,
} from '@mui/material';

import {
  AddToPhotos as AddToPhotosIcon,
  TableChartOutlined as TableChartOutlinedIcon,
  HistoryOutlined as HistoryOutlinedIcon,
  EventNote as EventNoteIcon,
} from '@mui/icons-material';

// eslint-disable-next-line import/no-extraneous-dependencies
import { SmartTextPrompt } from '@on24/chat-gpt';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { ReactComponent as AIContentIcon } from './AIContent.svg';
import { ReactComponent as HomeIcon } from './Home.svg';
import { ReactComponent as EventAdminIcon } from './EventAdmin.svg';
import { a11yHiddenCss } from '../../../designSystem';
import {
  REGISTRATION_URL,
  OVERVIEW_URL,
  CONSOLE_URL,
  ADMIN_URL,
  LEGACY_ARCHIVE_URL,
  LEGACY_WEBCASTS_URL,
  AI_CONTENT_URL,
} from './leftNav.constants';
import { sidebar } from './leftNav.styles';
import AppContext from '../../App.context';
import { GlobalEventSettingsContext } from '../../globalEventSettings.context';
import { SmartTextSettingsContext } from '../../smartTextSettings.context';

import NavItem from './NavItem.component';

const StyledIconButton = styled(IconButton)`
  width: 18px;
  height: 45px;
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  border: 1px solid ${({ theme }) => theme.palette.grey[40]};
  border-left: 0;
  border-radius: 0 4px 4px 0;
  background-color: ${({ theme }) => theme.palette.grey[3]};
  z-index: 99;
  &:hover {
    background-color: ${({ theme }) => theme.palette.grey[5]};
  }
  &:focus-visible {
    border: 1px solid ${({ theme }) => theme.palette.primary.main};
  }
`;

export const LeftNav = () => {
  const [open, setOpen] = useState(true);

  const localStorVal = localStorage.getItem('isShowSmartTextOnEventCreation');
  const initIsShowOnEventCreation = localStorVal === null || localStorVal !== 'false';
  const isRedirectingFromCreatePageLocalStorageVal = useMemo(() => localStorage.getItem('isRedirectingFromCreatePage'), []);
  const isOpenOnInit = initIsShowOnEventCreation && isRedirectingFromCreatePageLocalStorageVal === 'true';

  const {
    t, accountProperties, defaultFetchOptions, globalsConfig,
  } = useContext(AppContext);

  const { eventId, groupId } = useParams();

  const {
    isReg30 = false,
    isArchiveEnabled = false,
    eventType,
  } = useContext(GlobalEventSettingsContext);

  const { isAIContentTabEnabledFE } = globalsConfig;

  const {
    setIsSmartTextIconsEnabled,
    isSmartTextIconsEnabled,
    isSmartTextPromptsDialogEnabled,
  } = useContext(SmartTextSettingsContext);

  const {
    isNonEventAdminLogin,
    isHtml5BuilderEnabled,
    'auto-sfa-based-km': isACEClient, // EM: key moments = ACE client
  } = accountProperties?.accountProperties ?? {};

  const { pathname } = useLocation();
  const isCreatePage = !!pathname.includes('create');
  const isConsolePage = !!pathname.includes('console');
  const isEventSeries = !!pathname.includes('series');
  const showAIContentTab = useMemo(
    () => isAIContentTabEnabledFE && eventType !== 'recurring-simulive' && isACEClient && !isEventSeries,
    [isAIContentTabEnabledFE, eventType, isACEClient, isEventSeries],
  );

  useEffect(() => {
    let timeoutId;
    if (isConsolePage) {
      timeoutId = setTimeout(() => setOpen(false), 3000);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isConsolePage]);

  // Prevent SmartTextPrompt from opening when user refreshes the page.
  useEffect(() => {
    setTimeout(() => { localStorage.removeItem('isRedirectingFromCreatePage'); }, 5000);
  }, []);

  const handleDrawerOpen = () => setOpen(!open);

  return (
    <Drawer
      sx={{
        overflow: 'visible',
        marginTop: '0px',
        marginRight: open ? 0 : '-100px',
        transition: 'all .25s cubic-bezier(0.4, 0, 0.2, 1)',
        '& .MuiDrawer-paper': {
          flexDirection: 'row-reverse',
          boxSizing: 'border-box',
          position: 'relative',
          visibility: 'unset !important',
          overflow: 'visible',
          borderRight: 'none',
          backgroundColor: 'grey.0',
          zIndex: '99',
        },
      }}
      variant="persistent"
      anchor="left"
      open={open}
    >
      <nav css={sidebar}>
        <h2 css={a11yHiddenCss}>{t('MainNavigation')}</h2>
        <Stack alignItems="center" gap="10px">
          <NavItem
            to={isEventSeries ? '/webcast/webcasts?tab=event-series' : LEGACY_WEBCASTS_URL}
            label={t('Webinars')}
            icon={(<SvgIcon component={HomeIcon} viewBox="0 0 32 32" />)}
          />
          {isCreatePage && !isEventSeries && (
            <NavItem
              to="/webcast/create"
              label={t('CreateNew')}
              icon={(<AddToPhotosIcon />)}
            />
          )}
          {isCreatePage && isEventSeries && (
            <NavItem
              to="/webcast/series/create"
              label={t('Registration')}
              icon={(<EventNoteIcon />)}
            />
          )}
          {!isCreatePage && !isEventSeries && (
            <NavItem
              to={OVERVIEW_URL(eventId)}
              label={t('Overview')}
              icon={(<VisibilityIcon />)}
            />
          )}
          {!(isCreatePage && isEventSeries) && (
            <NavItem
              isNativeHtmlLink={!isReg30 && !isEventSeries}
              to={isEventSeries ? `/webcast/series/${groupId}` : REGISTRATION_URL(eventId)}
              label={t('Registration')}
              disabled={isCreatePage}
              icon={(<EventNoteIcon />)}
            />
          )}
          {!isEventSeries && isHtml5BuilderEnabled && (
            <NavItem
              to={CONSOLE_URL(eventId)}
              label={t('Console')}
              disabled={isCreatePage}
              icon={(<TableChartOutlinedIcon />)}
            />
          )}
          {showAIContentTab && (
            <NavItem
              to={AI_CONTENT_URL(eventId)}
              label={t('AIContent')}
              disabled={isCreatePage}
              icon={(
                <SvgIcon
                  component={AIContentIcon}
                  viewBox="0 0 32 32"
                  inheritViewBox
                  sx={{ '& path': { fill: 'currentColor' } }}
                />
              )}
            />
          )}
          {!isEventSeries && isArchiveEnabled && (
          <NavItem
            to={LEGACY_ARCHIVE_URL(eventId)}
            label={t('Archive')}
            disabled={isCreatePage}
            icon={(<HistoryOutlinedIcon />)}
          />
          )}
          {!isEventSeries && isNonEventAdminLogin && (
            <NavItem
              to={ADMIN_URL(eventId)}
              label={t('ShareEvent')}
              disabled={isCreatePage}
              icon={(<SvgIcon component={EventAdminIcon} viewBox="0 0 32 32" />)}
            />
          )}
          {!isEventSeries && isSmartTextPromptsDialogEnabled && (
            <SmartTextPrompt
              fetchOptions={defaultFetchOptions}
              appId="ELITE"
              promptName="OVERVIEW_PROMPTS"
              eventId={eventId}
              isOpenOnInit={isOpenOnInit}
              baseApiUrl="/webcast/webapi/aigenerator/prompts"
              onSubmitSuccess={() => { setIsSmartTextIconsEnabled(true); }}
              onFetchSuccess={(data) => {
                if (data?.promptList?.filter((prompt) => prompt?.response)?.length === data?.promptList?.length
                && !isSmartTextIconsEnabled) {
                  setIsSmartTextIconsEnabled(true);
                }
              }}
              i18nLabels={{
                iconLabel: t('SmartText'),
                dialogTitle: t('SmartText'),
                promptsTitle: t('TellUsAboutThisEvent'),
                loadingText: t('loading'),
                successMsg: t('successSmartTextMsg'),
                errorMsg: t('errorMsg'),
                smartTextDesc: t('smartTextDesc'),
                requiredQuestionFieldValidationMsg: t('requiredQuestionFieldValidationMsg'),
                showOnEventCreationLabel: t('showOnEventCreationLabel'),
                saveButtonLabel: t('Save'),
                closeButtonLabel: t('Close'),
              }}
            />
          )}
        </Stack>
      </nav>

      <StyledIconButton aria-label={t('toggleLeftNavigation')} onClick={handleDrawerOpen}>
        {open ? <ArrowLeftIcon /> : <ArrowRightIcon />}
      </StyledIconButton>

    </Drawer>
  );
};
